import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, useMediaQuery } from "../../../Styles";
import { NewSelect, NewInputList } from "../..";
import { NewFieldStyles, SelectFieldOption_Values, Pen, Section, ListStyle_Types, SystemTypes } from "../../../Definitions";
import { useAppSelector } from "../../../Store";
import { useDeviceInfo, TerminalTypesInfo } from "../../../Services";
import { useModalContext } from "../../LocationFeeder/_LocationFeeder/newSectionModalContext";

const NewLfSectionModalLvl2: React.FC = () => {
  const { t } = useTranslation();
  const mobileXs = useMediaQuery(`(max-width: 805px)`);

  const { state: { farm, domain, connection, error, section, systemType }, dispatch } = useModalContext()
  const { data: deviceInfoState, call: getDeviceInfo } = useDeviceInfo(connection);

 // eslint-disable-next-line no-unused-vars
  const terminalTypesState = useAppSelector(({graphQlData}) => (graphQlData.domain.terminalTypes.entities))
  const terminalTypes = useMemo(() => { return terminalTypesState[domain]?.data ?? [] },[domain, terminalTypesState])
  
  const { input: activatorInputBoolean, ...activatorInputConfig } = terminalTypes.find((terminalType: { terminal_type_id: number; }) => (terminalType.terminal_type_id === 82))!;
  const { input: waterOutputBoolean, ...waterOutputConfig } = terminalTypes.find((terminalType: { terminal_type_id: number; }) => (terminalType.terminal_type_id === 53))!;

  let terminalTypeId = 50;
  if (systemType === SystemTypes.freedaLf) {
    terminalTypeId = section.activator ? 81 : 80;
  }
  const { input, ...defaultOutputTerminalConfig} = terminalTypes.find((terminalTypes: { terminal_type_id: number; }) => (terminalTypes.terminal_type_id === terminalTypeId))!;

  const pens = { ...section, contents: section.contents as Pen[] };

  const deviceInfo = useMemo(() => {
    if (deviceInfoState[domain]) {
      return deviceInfoState[domain].data.map((deviceInfo) => ({id: deviceInfo.device_id, name: deviceInfo.name, systemId: deviceInfo.system_assignment, type: 1}))
    }
    return []
  },[deviceInfoState, domain])

  useEffect(() => {
    getDeviceInfo({fetchAmount: 500}, connection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Total number of pens
  const pensWithFeederOutput = (Section_pens: Pen[]) => Section_pens.filter((pen) => pen.feederOutput != null).length;
  const pensWithWaterOutput = (Section_pens: Pen[]) => Section_pens.filter((pen) => pen.waterOutput != null).length;
  const pensWithActivatorInput = (Section_pens: Pen[]) => Section_pens.filter((pen) => pen.activatorInput != null).length;
  const activePensCount = section.contents.length;
  const totalOutputs = section.water ? (activePensCount * 2) : activePensCount
  const totalInputs = section.activator ? activePensCount : 0
  const remainingOutputs = section.water ? (totalOutputs - (pensWithFeederOutput(pens.contents) + (pensWithWaterOutput(pens.contents)))) : (totalOutputs - pensWithFeederOutput(pens.contents)) 
  const remainingInputs = totalInputs - (pensWithActivatorInput(pens.contents))

  const handleValueChange = (section: Section, penIndex: number, newValue: string, counterKey: keyof Pen, terminalConfig: Omit<TerminalTypesInfo, "input">) => {
    const updatedPens = pens.contents.map((pen, index) => {
      if (index === penIndex) {
        return {
          ...pen,
          [counterKey]: newValue ? Number(newValue) : null,
          [counterKey+"Config"]: terminalConfig,
          blockedIos: {...pen.blockedIos, [counterKey]: false}
        };
      }
      return pen;
    });
    dispatch({section:{contents:updatedPens}})
  };

  // SelectField Options
  const ioOptions = deviceInfo.map((type) => ({
    label: type.name, 
    id: type.id
  }));

  // Handler for select changes in IO
  const handlePenIOChange = (penId: string, value?: SelectFieldOption_Values) => {
    const updatedPens = pens.contents.map((pen) =>
      pen.penId === penId ? { ...pen, io: value?.id ? Number(value.id) : null } : pen
    );
    dispatch({section:{contents: updatedPens}})
  };

  // #region | IO Fields
  const contents = ( section: Section, index: number ) : [string[], string[], ((newValue: string) => void)[], (string | number)[], string[], number[], number[], boolean[]] => {
    const inputFieldEntries: {
      label: string;
      placeholders: string;
      blocked: boolean;
      onClick: (newValue: string) => void;
      defaultValues: (string | number)[],
      valueTypes: string[],
      minValueNumbers: number[]
      maxValueNumbers: number[]
    }[] = [];

    inputFieldEntries.push({
      label: t("locationFeederPages.sections.feederOutput", { defaultValue: "Feeder Output" }),
      placeholders: "",
      onClick: (newValue: string) => handleValueChange(section, index, newValue, "feederOutput", defaultOutputTerminalConfig),
      defaultValues: [pens.contents[index].feederOutput ?? 0],
      valueTypes: ["number"],
      minValueNumbers: [0],
      maxValueNumbers: [99],
      blocked: pens.contents[index]?.blockedIos?.feederOutput ?? false, 
    });
    
    if (section.water) { inputFieldEntries.push({
      label: t("locationFeederPages.sections.waterOutput", { defaultValue: "Water Output" }),
      placeholders: "",
      onClick: (newValue: string) => handleValueChange(section, index, newValue, "waterOutput", waterOutputConfig),
      defaultValues: [pens.contents[index].waterOutput ?? 0],
      valueTypes: ["number"],
      minValueNumbers: [0],
      maxValueNumbers: [99],
      blocked: pens.contents[index]?.blockedIos?.waterOutput ?? false, 
    })}
    
    if (section.activator) { inputFieldEntries.push({
      label: t("locationFeederPages.sections.activatorInput", {defaultValue: "Activator Input"}),
      placeholders: "",
      onClick: (newValue: string) => handleValueChange(section, index, newValue, "activatorInput", activatorInputConfig),
      defaultValues: [pens.contents[index].activatorInput ?? 0],
      valueTypes: ["number"],
      minValueNumbers: [0],
      maxValueNumbers: [99],
      blocked: pens.contents[index]?.blockedIos?.activatorInput ?? false 
    })}
  
    const labels = inputFieldEntries.map((e) => e.label);
    const placeholders = inputFieldEntries.map((e) => e.placeholders)
    const onclicks = inputFieldEntries.map((e) => e.onClick);
    const defaultValues = inputFieldEntries.flatMap((e) => e.defaultValues);
    const valueTypes = inputFieldEntries.flatMap((e) => e.valueTypes);
    const minValueNumbers = inputFieldEntries.flatMap((e) => e.minValueNumbers);
    const maxValueNumbers = inputFieldEntries.flatMap((e) => e.maxValueNumbers);
    const blocked = inputFieldEntries.flatMap((entry) => entry.blocked);
    return [labels, placeholders, onclicks, defaultValues, valueTypes, minValueNumbers, maxValueNumbers, blocked];
  };
  
  const ioFields = pens.contents.filter((pen) => pen.penId !== null).map((pen, index) => {
    const [labels, placeholders, onChanges, defaultValues, valueTypes, minValueNumbers, maxValueNumbers, blocked] = contents(section, index);
    const ioFieldList = (
      <NewInputList
        placeholders={placeholders}
        headers={labels}
        isBlocked={blocked}
        onChanges={onChanges}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greyTertiary,
          fieldWidth: mobileXs ? "100%" : "180px",
        }}
        valueOverrides={{
          defaultValues,
          valueTypes,
          minValueNumbers,
          maxValueNumbers
        }}
        listOverrides={{
          listStyle: mobileXs ? ListStyle_Types.vertical : ListStyle_Types.horizontal,
          listGap: "9px",
        }}
      />
    );

    return (
      <div key={pen.penId} style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
        <NewSelect
          key={"select_" + pen.penId}
          isSearchable
          isRequired
          header={`${t("locationFeederPages.locationFeeder.pen", {defaultValue: "Pen"})} ${pen.name}`}
          placeholder={t("devices.chooseIo", { defaultValue: "Choose I/O" })}
          options={ioOptions}
          onChange={(selectedOption) => { if (pen.penId) {handlePenIOChange(pen.penId, selectedOption)}}}
          valueOverrides={{defaultValue: ioOptions.find((option) => option.id === pen.io)}}
        />
        <div style={{ width: "calc(100% - 20px)", height: "fit-content", backgroundColor: colors.greyLightSecondary, padding: "10px 10px", borderRadius: "10px" }}>
          {ioFieldList}
        </div>
      </div>
    );
  });

  // #endregion

  return (
    <div style={{ width: "100%", display: "grid", gridTemplateRows: mobileXs ? "2px 1fr 2px 80px" : "2px 1fr 2px 45px", gap: "5px", gridTemplateColumns: "1fr", height: "100%" }}>
      <div style={{ gridArea: "1/1/2/2",  borderBottom: "2px solid "+colors.blueDarkSecondary }}/>
      
      <div style={{ gridArea: "2/1/3/2", padding: "10px", maxHeight: "40vh", overflowY: "auto", boxSizing: "border-box", overflowX: "hidden", scrollbarWidth: "thin", scrollbarColor: colors.blueDarkSecondary + colors.transparent, display: "flex", alignItems: "center", justifyContent: "start", flexDirection: "column", gap: "15px" }}>
        {ioFields}
      </div>
      
      <div style={{ gridArea: "3/1/4/2", borderBottom: "2px solid " + colors.blueDarkSecondary }} />
      
      <div style={{ gridArea: "4/1/5/2", padding: "10px", boxSizing: "border-box", display: "flex", flexDirection: mobileXs ? "column" : "row", gap: mobileXs ? "10px" : undefined, alignItems: "center", justifyContent: "space-around" }}>
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"}}>
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{`${t("devices.remainingOutputs", {defaultValue:"Remaining Outputs"})}:`}</p>
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary, backgroundColor: remainingOutputs === 0 ? colors.systemGreen : colors.systemRed, padding: "4px 5px", borderRadius: "6px"}}>{remainingOutputs}</p>
        </div>
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"}}>
            <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{`${t("devices.remainingInputs", {defaultValue:"Remaining Inputs"})}:`}</p>
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary, backgroundColor: remainingInputs === 0 ? colors.systemGreen : colors.systemRed, padding: "4px 5px", borderRadius: "6px"}}>{remainingInputs}</p>
        </div>
      </div>
      
      <div style={{ gridArea: "5/1/6/2", borderBottom: "2px solid " + colors.blueDarkSecondary }} />
    
    </div>
  );
};

export default NewLfSectionModalLvl2;
